"use client";

import React from "react";
import { motion } from "framer-motion";
import { PortableText } from "@portabletext/react";
import { richTextComponent } from "@/sanity/sanityPortableText";
import Chip from "@/components/global/ui/Chip";
import type { PageHomeSchema } from "@/sanity/pages/PageHomeTemplate";

import lines from "@/public/decos/lines-deco-black.svg";
import Image from "next/image";

import dashScreenshot from "@/public/home/dashboard-screenshot.jpg";

const TRANSITION_EASING = [0.22, 1, 0.36, 1] as const;
const VIEWPORT = { once: true, margin: "-50px" } as const;
const REVEAL_CHIP = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: VIEWPORT,
} as const;
const REVEAL_TITLE = {
  initial: { opacity: 0, y: 32 },
  whileInView: { opacity: 1, y: 0 },
  viewport: VIEWPORT,
} as const;
const CHIP_TRANSITION = { duration: 0.7, ease: [...TRANSITION_EASING] };
const TITLE_TRANSITION = { duration: 0.8, ease: [...TRANSITION_EASING], delay: 0.1 };

function CustomizeSectionDesktop({ data }: { data: PageHomeSchema }) {
  const { customizeChip, customizeTitle } = data;

  return (
    <section className="hidden lg:block relative bg-gradient-to-b to-white border-t from-white/0 via-white/90 z-[5] border-t-black/10 lg:h-[162.875rem]">
      <div className="absolute top-0 h-full w-[.125rem] bg-black/10 left-[29.5rem]" />
      <div className="absolute top-0 h-full w-[.125rem] bg-black/10 right-[29.5rem]" />
      <div className="absolute right-0 w-full top-[31.625rem] h-[.125rem] bg-black/10" />
      <div className="absolute right-0 w-full bottom-[30rem] h-[.125rem] bg-black/10" />

      <div className="absolute bottom-0 w-full bg-gradient-to-b h-[20%] from-transparent to-white z-[99]" />
      <div className="absolute right-0 z-[5] top-[31.625rem] w-[30rem]">
        <Image
          src={lines}
          alt=""
          className="w-full"
        />
      </div>
      <div className="absolute left-0 z-[5] top-[31.625rem] w-[30rem]">
        <Image
          src={lines}
          alt=""
          className="w-full"
        />
      </div>

      <div className="container flex flex-col pt-[5rem] gap-[2rem]">
        {customizeChip && (
          <motion.div
            className="mx-auto w-fit"
            {...REVEAL_CHIP}
            transition={CHIP_TRANSITION}>
            <Chip
              text={customizeChip}
              animatedIcon
              spinnerSize={32}
              spinnerFps={3}
              spinnerVariant="pulse"
            />
          </motion.div>
        )}

        {customizeTitle && (
          <motion.div
            className="richtext prose-h1:bg-gradient-to-b prose-h1:from-black prose-h1:to-[#353535] prose-h1:bg-clip-text prose-h1:text-transparent"
            {...REVEAL_TITLE}
            transition={TITLE_TRANSITION}>
            <PortableText
              value={customizeTitle}
              components={richTextComponent}
            />
          </motion.div>
        )}
      </div>

      <div className="w-[min(180rem,100%)] absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-[49%] h-[101.25rem]">
        <Image
          src={dashScreenshot}
          alt=""
          quality={90}
        />
      </div>
    </section>
  );
}

function CustomizeSectionMobile({ data }: { data: PageHomeSchema }) {
  const { customizeChip, customizeTitle } = data;

  return (
    <section className="lg:hidden relative bg-gradient-to-b to-white border-t from-white/0 via-white/90 py-[6rem] z-[5] -mt-[10rem] border-t-black/10">
      <div className="container flex flex-col pt-[5rem] gap-[2rem]">
        {customizeChip && (
          <div className="mx-auto w-fit">
            <Chip
              text={customizeChip}
              spinnerSize={32}
              spinnerFps={3}
              spinnerVariant="pulse"
            />
          </div>
        )}

        {customizeTitle && (
          <div className="richtext prose-h1:bg-gradient-to-b prose-h1:from-black prose-h1:to-[#353535] prose-h1:bg-clip-text prose-h1:text-transparent">
            <PortableText
              value={customizeTitle}
              components={richTextComponent}
            />
          </div>
        )}
      </div>

      <Image
        src={dashScreenshot}
        alt=""
        className="mx-auto mt-[2rem] w-[95%]"
      />
    </section>
  );
}

export default function CustomizeSection({ data }: { data: PageHomeSchema }) {
  return (
    <>
      <CustomizeSectionDesktop data={data} />
      <CustomizeSectionMobile data={data} />
    </>
  );
}
